import { useState, type ComponentType, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Button as AriaButton, Dialog, DialogTrigger, Popover } from 'react-aria-components'
import { ChevronRight, ListFilter } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface InboxFilterCategory {
  id: string
  label: string
  icon: ComponentType<{ className?: string; strokeWidth?: number }>
  /** Number of selected values, or 1 for an active text filter */
  activeCount: number
  /** Panel shown beside the menu while this category is open */
  content: ReactNode
}

interface InboxFilterMenuProps {
  categories: InboxFilterCategory[]
}

export function InboxFilterMenu({ categories }: InboxFilterMenuProps) {
  const { t } = useTranslation()
  // Hover previews a category until one is clicked; a click pins it so moving
  // the pointer to type in the panel cannot swap the panel away.
  const [pinnedId, setPinnedId] = useState<string | null>(null)
  const [hoveredId, setHoveredId] = useState<string | null>(null)
  const openId = pinnedId ?? hoveredId
  const activeFilters = categories.filter((category) => category.activeCount > 0).length

  return (
    <DialogTrigger
      onOpenChange={(isOpen) => {
        if (isOpen) return
        setPinnedId(null)
        setHoveredId(null)
      }}
    >
      <AriaButton
        aria-label={t('resources.conversations.inbox.filters.menu')}
        className={cn(
          'outline-focus-ring relative flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-md text-gray-600 hover:bg-gray-100 focus-visible:outline-2 dark:text-gray-400 dark:hover:bg-gray-800',
          activeFilters > 0 && 'text-gray-900 dark:text-gray-100'
        )}
      >
        <ListFilter className="size-5" strokeWidth={2.5} aria-hidden="true" />
        {activeFilters > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex size-4 items-center justify-center rounded-full bg-gray-700 text-[10px] leading-none font-medium text-white">
            {activeFilters}
          </span>
        )}
      </AriaButton>
      <Popover
        placement="bottom end"
        offset={4}
        className="w-50 overflow-visible rounded-lg bg-white shadow-lg ring-1 ring-gray-200 outline-hidden dark:bg-gray-900 dark:ring-gray-700"
      >
        <Dialog
          aria-label={t('resources.conversations.inbox.filters.menu')}
          className="outline-hidden"
        >
          <ul className="py-1">
            {categories.map((category) => {
              const { id, label, icon: Icon, activeCount } = category
              const isOpen = openId === id

              return (
                <li key={id} className="relative">
                  <button
                    type="button"
                    onClick={() => setPinnedId(id)}
                    onMouseEnter={() => !pinnedId && setHoveredId(id)}
                    aria-expanded={isOpen}
                    className={cn(
                      'flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-sm leading-[18px] text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-800',
                      isOpen && 'bg-gray-50 dark:bg-gray-800'
                    )}
                  >
                    <Icon className="size-4 shrink-0" strokeWidth={2.5} aria-hidden="true" />
                    <span className="min-w-0 flex-1 truncate">{label}</span>
                    {activeCount > 0 && (
                      <span className="flex size-4.5 items-center justify-center rounded-full bg-gray-700 text-[10px] leading-none font-medium text-white">
                        {activeCount}
                      </span>
                    )}
                    <ChevronRight className="size-4 shrink-0" strokeWidth={2.5} aria-hidden="true" />
                  </button>
                  {isOpen && (
                    <div
                      role="group"
                      aria-label={label}
                      className="absolute top-0 left-full z-10 ml-1 w-60 overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-700"
                    >
                      <div key={id}>{category.content}</div>
                    </div>
                  )}
                </li>
              )
            })}
          </ul>
        </Dialog>
      </Popover>
    </DialogTrigger>
  )
}
